import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/layout";
import Button from "../components/button";
import { getProjectById } from "../data/projects";
import projects from "../data/projects";

// Slot definitions: each has a filename and label
const SLOTS = [
  { slot: "packaging",     file: "packaging.jpg",     label: "Packaging" },
  { slot: "cup-holder",    file: "cup-holder.jpg",    label: "Cup Holder" },
  { slot: "paper-bag",     file: "paper-bag.jpg",     label: "Paper Bag & Coffee Cup" },
  { slot: "business-card", file: "business-card.jpg", label: "Business Card" },
  { slot: "stickers",      file: "stickers.jpg",      label: "Stickers" },
  { slot: "menu",          file: "menu.jpg",          label: "Menu Design" },
  { slot: "logo",          file: "logo.png",          label: "Primary Logo" },
  { slot: "apparel",       file: "apparel.jpg",       label: "Apparel" },
  { slot: "signage",       file: "signage.jpg",       label: "Signage" },
];

const getSlot = (name) => SLOTS.find((s) => s.slot === name);

/*
 * CsBlock — tries to load /case-studies/{projectId}/{filename}
 * Shows the image if it exists, gray placeholder with label if not.
 */
const CsBlock = ({ projectId, slotName, className = "", bgColor }) => {
  const [failed, setFailed] = useState(false);
  const slot = getSlot(slotName);
  const style = bgColor ? { backgroundColor: bgColor } : undefined;

  if (!slot) return <div className={`cs-grid__block ${className}`} style={style} />;

  const src = `${process.env.PUBLIC_URL}/case-studies/${projectId}/${slot.file}`;

  if (failed) {
    return (
      <div className={`cs-grid__block ${className}`} style={style}>
      </div>
    );
  }

  return (
    <div className={`cs-grid__block cs-grid__block--has-image ${className}`} style={style}>
      <img
        src={src}
        alt={slot.label}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const SummaryText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`project-page__summary ${expanded ? "project-page__summary--expanded" : ""}`}>
      <p className="project-page__summary-text">{text}</p>
      <button
        className="project-page__read-more"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "Read less" : "Read more"}
      </button>
    </div>
  );
};

export default function ProjectPage() {
  const { projectId } = useParams();
  const project = getProjectById(projectId);

  if (!project) {
    return (
      <Layout>
        <div className="container py-5" style={{ textAlign: "center" }}>
          <h1>Project not found</h1>
          <Button to="/work">Back to Our Work</Button>
        </div>
      </Layout>
    );
  }

  const currentIndex = projects.findIndex((p) => p.id === projectId);
  const prevProject = projects[(currentIndex - 1 + projects.length) % projects.length];
  const nextProject = projects[(currentIndex + 1) % projects.length];

  const colors = project.caseStudy?.colors || [];
  const gridBg = project.caseStudy?.gridBg || "#E8E8ED";

  return (
    <Layout>
      <div className="project-page">
        {/* Back link */}
        <div className="container">
          <Link to="/work" className="project-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            All Projects
          </Link>
        </div>

        {/* Header */}
        <div className="container">
          <div className="project-page__header">
            <span className="project-page__category">{project.category}</span>
            <h1 className="project-page__title">{project.title}</h1>
            <SummaryText text={project.summary} />
          </div>
        </div>

        {/* Case study grid */}
        <div className="container">
          <div className="cs-grid" key={projectId}>
            {/* Main grid: tall packaging left, stacked right */}
            <div className="cs-grid__row cs-grid__row--main">
              <CsBlock projectId={projectId} slotName="packaging" className="cs-grid__block--tall" bgColor={gridBg} />
              <CsBlock projectId={projectId} slotName="cup-holder" bgColor={gridBg} />
              <CsBlock projectId={projectId} slotName="paper-bag" bgColor={gridBg} />
              <div className="cs-grid__split">
                <CsBlock projectId={projectId} slotName="business-card" bgColor={gridBg} />
                <CsBlock projectId={projectId} slotName="menu" bgColor={gridBg} />
              </div>
            </div>

            {/* Full-width logo */}
            <div className="cs-grid__row cs-grid__row--logo">
              <CsBlock projectId={projectId} slotName="logo" className="cs-grid__block--logo" bgColor={gridBg} />
            </div>

            {/* Row 4: Color swatches */}
            {colors.length > 0 && (
              <div className="cs-grid__row cs-grid__row--colors">
                {colors.map((c, i) => (
                  <div key={i} className="cs-grid__swatch">
                    <div className="cs-grid__swatch-block" style={{ backgroundColor: c.value }} />
                    <span className="cs-grid__swatch-name">{c.name}</span>
                    <span className="cs-grid__swatch-value">{c.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Row 5: Two more deliverables */}
            <div className="cs-grid__row cs-grid__row--duo">
              <CsBlock projectId={projectId} slotName="apparel" bgColor={gridBg} />
              <CsBlock projectId={projectId} slotName="signage" bgColor={gridBg} />
            </div>
          </div>
        </div>

        {/* Services + Outcomes */}
        <div className="container">
          <div className="project-page__details">
            <div className="project-page__detail-col">
              <h3>Services</h3>
              <ul>
                {project.services.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
            <div className="project-page__detail-col">
              <h3>Outcomes</h3>
              <ul>
                {project.outcomes.map((o) => (
                  <li key={o}>{o}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Prev / Next */}
        <div className="container">
          <div className="project-page__nav">
            <Link to={`/work/${prevProject.id}`} className="project-page__nav-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>{prevProject.title}</span>
            </Link>
            <Link to={`/work/${nextProject.id}`} className="project-page__nav-btn project-page__nav-btn--next">
              <span>{nextProject.title}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>

        {/* CTA */}
        <div className="container" style={{ textAlign: "center", padding: "40px 0 0" }}>
          <Button to="/contact">Work with us</Button>
        </div>
      </div>
    </Layout>
  );
}
